import React, {ComponentType} from "react";
import MenuOptions from "shared-components/booking-options-section/MenuOptions/index";
import {menuOptionType} from "shared-components/booking-options-section/MenuOption/types";
import {IParentMenuOption} from "shared-components/booking-options-section/ChildMenuOptionOverlay/types";
import {IDispatchFromProps, IStateFromProps} from "shared-components/booking-options-section/MenuOptions/types";
import { LocaleService } from 'app/services/locale/locale.service';
import {IContainerProps, IServices} from './types';
import ReactUtilsService from "app/services/reactUtils.service";
import useForceUpdate from 'use-force-update';
import MenuOptionsService from "app/services/menuOptions/menuOptions.service";
import {IntlProvider} from 'react-intl/dist';
import MenuOptionReducerService from "app/fakeReducer/menuOption.reducer";
import AppThemeService from "app/services/theme/appTheme.service";
import {
  IBookingMenuOption,
  IBookingMenuOptionExtrasUpdater,
  IServicePaymentOption,
  IWidgetTheme,
  PricingTypes
} from "shared-types/index";
import MenuOptionActionService from "app/actions/menuOption.actions";
import ScopedCssBaseline from '@material-ui/core/ScopedCssBaseline';

/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import BookingService from "external-services/booking-service";
import store from 'appRoot/Store';
import { Provider } from 'react-redux';

const NS = 'MenuOptionsContainer';

const propsKeys: string[] = keys<IStateFromProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const servicesKeys: string[] = keys<IServices>();

const themeVal: IWidgetTheme = AppThemeService.getAppThemeAsWidgetTheme();

const ThemeWrap = function (all: IContainerProps & IDispatchFromProps & IServices) {

  const forceUpdate = useForceUpdate();

  // const {
  //
  // }: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices

  const {
    paymentDetails, maxCovers, serviceId, currency, bookingTimes,
    bookingDate, updateBookingOption, handleMaxCovers, isUpsell
  }: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  const props: IStateFromProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IStateFromProps & IDispatchFromProps;
  props.theme = themeVal;
  const state = MenuOptionReducerService.getState();

  const upsellOptions: IServicePaymentOption[] = isUpsell && paymentDetails && paymentDetails.optionsUpsell ? paymentDetails.optionsUpsell : [];

  const bookingSelectedOptions = state.booking.selectedMenuOptions;

  const options: IServicePaymentOption[] = isUpsell ? upsellOptions : paymentDetails.options; // if upsell present - send upsell options instead of normal BO

  const singleMenuPerBooking = isUpsell ? false : paymentDetails.singleMenuPerBooking; // if upsell is present it will always be spinner and not radio buttons

  const covers: number = maxCovers ? maxCovers : 0;
  props.selectedMenuOptions = BookingService.getBookingOptions(
    options, bookingSelectedOptions, covers, singleMenuPerBooking, isUpsell
  );
  props.type = paymentDetails.singleMenuPerBooking
    ? menuOptionType.radioButtons
    : (
      paymentDetails.pricingType === PricingTypes.PerPax
        ? menuOptionType.spinners
        : menuOptionType.checkboxes
      );
  props.menuOptions = paymentDetails.options as IServicePaymentOption[];
  props.useMuiGrid = false; // tells it to use flexbox layout instead of material ui grid
  props.isUpsell = isUpsell;

  MenuOptionReducerService.setState({...state, currency});

  // if pricing is per booking, then handleMaxCovers(success) gets called to set the pricing as valid
  function pricingPerBookingSetToValid() {
    if (paymentDetails.pricingType === PricingTypes.PerBooking) {
      handleMaxCovers(true);
    }
  }

  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IDispatchFromProps = {
    handleRadioSelect: async (menuOptionId: string) => {
      const selectedMenuOptions: IBookingMenuOption[] = MenuOptionsService.getSelectedMenuOptionFromId(menuOptionId, paymentDetails, covers);
      await MenuOptionActionService.updateSelectedMenuOption(selectedMenuOptions);
      const newState = MenuOptionReducerService.getState();
      updateBookingOption(newState.booking);
      forceUpdate();

    },
    handleMultiBookingOptionUpdate: async (selectedMenuOptions: IBookingMenuOption[], isUpsell) => {
      await MenuOptionActionService.updateSelectedMenuOption(selectedMenuOptions, isUpsell);
      const newState = MenuOptionReducerService.getState();
      pricingPerBookingSetToValid();
      updateBookingOption(newState.booking);
      forceUpdate();
    },
    handleActiveChildMenuOption: async (menuOption: IParentMenuOption) => {
      await MenuOptionActionService.updateActiveChildMenuOption(menuOption);
      forceUpdate();
    },

    handleDone: async (data: IBookingMenuOptionExtrasUpdater) => {
      await MenuOptionActionService.updateMenuOptionExtras(data, serviceId, bookingDate, bookingTimes);
      const newState = MenuOptionReducerService.getState();
      pricingPerBookingSetToValid();
      updateBookingOption(newState.booking);
      forceUpdate();
    },

    handleMaxCovers(isSuccess: boolean) {
      handleMaxCovers(isSuccess);
    }
  }

  return ReactUtilsService.containerWrap(
    <Provider store={store}>
      <IntlProvider locale={LocaleService.getLocale()}>
        <ScopedCssBaseline>
          <MenuOptions {...props} {...handlers} />
        </ScopedCssBaseline>
      </IntlProvider>
    </Provider>
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AMenuOptions: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
